import '@data/Simulations/pipeline/blocks/ProjectionBlock';
import type { ProjectionParams } from '@data/Simulations/pipeline/blocks/ProjectionBlock';
import { ProjectionType } from '@data/Simulations/pipeline/stages/transformations/ProjectStage';
import { UMInputNumber } from '@urbanmetrix/ui-kit';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { PROJECTION_OPTIONS, getProjectionTooltip } from '@gui/constants/Projection';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import type { BlockParameters } from '@visualizer/types';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePipelineBlockParams } from './usePipelineBlockParams';

type ProjectionParametersProps = {
    blockId: string;
};

const ANGLE_MIN = -90;
const ANGLE_MAX = 90;

const clampAngle = (value: number) => Math.max(ANGLE_MIN, Math.min(ANGLE_MAX, value));

export const getProjectionDefaultParams = (): Pick<ProjectionParams, 'projectionType' | 'projectionAngle'> => ({
    projectionType: ProjectionType.NO_PROJECTION,
    projectionAngle: 0,
});

const ProjectionParameters: FC<ProjectionParametersProps> = ({ blockId }) => {
    const params = usePipelineBlockParams<ProjectionParams>(blockId);
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);
    const defaultParams = useMemo(() => getProjectionDefaultParams(), []);
    const updateParams = useCallback(
        (partial: Partial<ProjectionParams>) => {
            pipelineController.updateBlockParams(blockId, partial as BlockParameters);
        },
        [pipelineController, blockId]
    );

    useEffect(() => {
        if (!params) {
            updateParams(defaultParams);
            return;
        }

        const missing: Partial<ProjectionParams> = {};
        if (params.projectionType === undefined) {
            missing.projectionType = defaultParams.projectionType;
        }
        if (params.projectionAngle === undefined) {
            missing.projectionAngle = defaultParams.projectionAngle;
        }

        if (Object.keys(missing).length > 0) {
            updateParams(missing);
        }
    }, [params, updateParams, defaultParams]);

    const projectionType = params?.projectionType ?? defaultParams.projectionType;
    const projectionAngle = clampAngle(params?.projectionAngle ?? defaultParams.projectionAngle!);
    const [angleInput, setAngleInput] = useState<string>(String(projectionAngle));

    useEffect(() => {
        setAngleInput(String(projectionAngle));
    }, [projectionAngle]);

    const handleProjectionChange = (_: unknown, value: string) => {
        if (!value) return;
        updateParams({ projectionType: Number(value) as ProjectionType });
    };

    const commitAngle = (value: number) => {
        const clamped = clampAngle(value);
        setAngleInput(String(clamped));
        updateParams({ projectionAngle: clamped });
    };

    return (
        <Box>
            <RadioGroup
                row
                className="panel-compact-control-stack"
                sx={{ gap: 0.5 }}
                value={projectionType}
                onChange={handleProjectionChange}
            >
                {PROJECTION_OPTIONS.map((option) => (
                    <CustomTooltip side="right" key={option.value} mode="icon" title={getProjectionTooltip(option.value)}>
                        <FormControlLabel value={option.value} control={<Radio size="small" />} label={option.label} />
                    </CustomTooltip>
                ))}
            </RadioGroup>

            {projectionType === ProjectionType.ORTHOGONAL_PLANE && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" gutterBottom>
                        Projection angle (°)
                    </Typography>
                    <UMInputNumber
                        value={angleInput}
                        defaultValue={0}
                        min={ANGLE_MIN}
                        max={ANGLE_MAX}
                        maxDecimals={1}
                        emitRawOnChange
                        onChange={(_, value, valid) => {
                            if (typeof value === 'string') {
                                setAngleInput(value);
                                const normalized = value.trim().replace(',', '.');
                                if (!normalized || normalized === '-' || normalized.endsWith('.')) {
                                    return;
                                }

                                const parsed = Number(normalized);
                                if (!Number.isNaN(parsed)) {
                                    commitAngle(parsed);
                                }
                                return;
                            }

                            if (typeof value === 'number' && !Number.isNaN(value)) {
                                commitAngle(value);
                            }

                            if (valid === false) {
                                setAngleInput(String(projectionAngle));
                            }
                        }}
                        width={65}
                        inputPadding="8px 6px"
                        inputFontSize="0.875rem"
                        inputClassName="panel-textfield"
                    />
                </Box>
            )}
        </Box>
    );
};

export default ProjectionParameters;
